import { useCallback, useEffect, useRef, useState } from 'react';
import { indexOf } from '../../../games/spec/interpreter.ts';
import type { GameSpec } from '../../../games/spec/schema.ts';
import type { Seat } from '../../../shared/domain.ts';
import { Column } from './Column.tsx';
import { Disc } from './Disc.tsx';
import { cellLabel, markerOf, occupantLabel, rowRenderOrder, themeVariables } from './presentation.ts';
import type { Board as BoardModel } from './useMatch.ts';

/** A piece somebody else thinks should be there — the review's suggestion. */
export interface BoardAnnotation {
  readonly column: number;
  readonly seat: Seat;
  /** Read aloud for the annotated cell, in place of "empty". */
  readonly label: string;
}

/**
 * A number written on an empty square, with the seat it belongs to.
 *
 * The parity trainer's whole subject: how many empty squares sit under this
 * one, and which player the count therefore gives it to. Kept as a general
 * per-cell note rather than a parity-shaped prop so the board stays a board.
 */
export interface CellNote {
  /** What to draw in the square. A gap count, so one or two characters. */
  readonly text: string;
  /** Whose square the note says it is. Null leaves it untinted. */
  readonly seat: Seat | null;
  /** Appended to the cell's spoken label, because a number nobody hears is a number half the audience does not get. */
  readonly label: string;
  /** The square under discussion, drawn with a ring around it. */
  readonly focus?: boolean;
}

export interface BoardProps {
  /** The game being played. The only source of geometry, theme and labels. */
  readonly spec: GameSpec;
  readonly board: BoardModel;
  readonly yourSeat: Seat | null;
  readonly legalColumns: readonly number[];
  readonly pendingColumn: number | null;
  readonly lastMoveIndex: number | null;
  readonly winningLine: readonly number[] | null;
  readonly interactive: boolean;
  readonly onDrop: (column: number) => void;
  /**
   * A ghost piece the viewer did not place. Used by the review screen for the
   * engine's suggestion; absent everywhere else.
   */
  readonly annotation?: BoardAnnotation | null;
  /**
   * Whether to render the per-column buttons. The review screen turns them off:
   * there is nothing to drop into a finished game, and seven disabled buttons
   * would be seven dead tab stops between the reader and the move list.
   */
  readonly columnControls?: boolean;
  /**
   * Numbers written on empty squares, keyed by cell index. Absent everywhere
   * but the parity trainer, where the numbers are the lesson.
   */
  readonly notes?: ReadonlyMap<number, CellNote> | null;
}

/** Bottom-most empty row in a column, or -1 when full. */
function landingRow(spec: GameSpec, board: BoardModel, column: number): number {
  for (let row = 0; row < spec.rules.board.rows; row += 1) {
    if (board[indexOf(spec.rules, row, column)] === 0) return row;
  }
  return -1;
}

function discsIn(spec: GameSpec, board: BoardModel, column: number): number {
  let count = 0;
  for (let row = 0; row < spec.rules.board.rows; row += 1) {
    if (board[indexOf(spec.rules, row, column)] !== 0) count += 1;
  }
  return count;
}

/**
 * Presentational. It takes props and emits `onDrop`; it never touches the socket.
 *
 * Two overlapping structures on purpose: a `role="grid"` that describes the
 * position for assistive technology, and one button per column on top that
 * provides the action. Screen readers get accurate state, keyboard users get one
 * tab stop and arrow keys, and neither has to fake the other.
 *
 * Everything dimensional comes from `spec`. There is no `COLS` here any more,
 * because there is no longer one board: an authored 9x7 renders as 9x7, themed
 * in its author's colours, described in its author's words.
 */
export function Board({
  spec,
  board,
  yourSeat,
  legalColumns,
  pendingColumn,
  lastMoveIndex,
  winningLine,
  interactive,
  onDrop,
  annotation = null,
  columnControls = true,
  notes = null,
}: BoardProps) {
  const { cols, rows } = spec.rules.board;
  const { labels } = spec.presentation;

  const [focusedColumn, setFocusedColumn] = useState(() => Math.floor(cols / 2));
  const [hoveredColumn, setHoveredColumn] = useState<number | null>(null);
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  // A narrower board can leave the roving tabindex pointing past its last
  // column, which would strand the keyboard user on a button that is not there.
  useEffect(() => {
    setFocusedColumn((current) => (current < cols ? current : Math.floor(cols / 2)));
  }, [cols]);

  const registerRef = useCallback((column: number, element: HTMLButtonElement | null) => {
    refs.current[column] = element;
  }, []);

  const moveFocus = useCallback(
    (column: number) => {
      const clamped = Math.max(0, Math.min(cols - 1, column));
      setFocusedColumn(clamped);
      refs.current[clamped]?.focus();
    },
    [cols],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          moveFocus(focusedColumn - 1);
          return;
        case 'ArrowRight':
          event.preventDefault();
          moveFocus(focusedColumn + 1);
          return;
        case 'Home':
          event.preventDefault();
          moveFocus(0);
          return;
        case 'End':
          event.preventDefault();
          moveFocus(cols - 1);
          return;
        default:
      }
    },
    [cols, focusedColumn, moveFocus],
  );

  // The ghost previews exactly where a piece would land — the whole reason the
  // column, rather than the cell, is the target. A spec may switch it off.
  const previewEnabled = spec.presentation.layout.landingPreview === 'ghost';
  const previewColumn = pendingColumn ?? (interactive ? hoveredColumn : null);
  const previewRow =
    previewColumn === null || !previewEnabled ? -1 : landingRow(spec, board, previewColumn);
  const previewIndex =
    previewColumn !== null && previewRow >= 0 ? indexOf(spec.rules, previewRow, previewColumn) : null;

  // The suggestion lands by the same gravity as a real move, so it appears in
  // the cell the piece would actually have occupied — not on a column header.
  const annotationRow = annotation === null ? -1 : landingRow(spec, board, annotation.column);
  const annotationIndex =
    annotation !== null && annotationRow >= 0
      ? indexOf(spec.rules, annotationRow, annotation.column)
      : null;

  const renderedRows = rowRenderOrder(spec).map((row) => {
    const cells = [];
    for (let column = 0; column < cols; column += 1) {
      const index = indexOf(spec.rules, row, column);
      const cell = board[index] ?? 0;
      const isPreview = previewIndex === index && cell === 0 && yourSeat !== null;
      const isSuggested = !isPreview && annotationIndex === index && cell === 0;
      const occupant =
        isSuggested && annotation !== null
          ? annotation.label
          : occupantLabel(labels, cell, yourSeat);
      // The count belongs to the square it describes, so it is spoken as part
      // of that square rather than announced separately.
      const note = cell === 0 ? (notes?.get(index) ?? null) : null;
      const spoken =
        note === null ? occupant : `${occupant}, ${note.label}`;
      cells.push(
        <div
          key={index}
          role="gridcell"
          className={`cell${note?.focus ? ' cell--counted' : ''}`}
          aria-label={cellLabel(labels, row, column, spoken)}
        >
          {note !== null ? (
            /*
              Colour is never the only channel here either: the chip takes the
              same filled/ring distinction the discs use, from the same authored
              theme, so whose square it is survives greyscale and forced colours.
            */
            <span
              className={
                note.seat === null
                  ? 'cell__note'
                  : `cell__note cell__note--seat${note.seat} cell__note--${markerOf(spec, note.seat)}`
              }
              aria-hidden="true"
            >
              {note.text}
            </span>
          ) : null}
          {isSuggested && annotation !== null ? (
            <Disc
              cell={annotation.seat}
              marker={markerOf(spec, annotation.seat)}
              variant="suggested"
            />
          ) : isPreview ? (
            <Disc
              cell={yourSeat}
              marker={markerOf(spec, yourSeat)}
              variant={pendingColumn === previewColumn ? 'pending' : 'ghost'}
            />
          ) : (
            <Disc
              cell={cell}
              marker={cell === 0 ? 'filled' : markerOf(spec, cell)}
              justLanded={index === lastMoveIndex}
              inWinningLine={winningLine?.includes(index) ?? false}
            />
          )}
        </div>,
      );
    }
    return (
      <div key={row} role="row" className="board__row">
        {cells}
      </div>
    );
  });

  return (
    <div
      className={`board${interactive ? ' board--interactive' : ''}`}
      // The authored theme wins over the stylesheet here, and only here: these
      // are the same custom property names `styles.css` declares, so the CSS
      // values become the fallback for anything the spec leaves unsaid.
      style={{
        ...themeVariables(spec),
        '--board-cols': cols,
        '--board-rows': rows,
      } as React.CSSProperties}
      onKeyDown={onKeyDown}
      onMouseLeave={() => setHoveredColumn(null)}
    >
      <div
        role="grid"
        aria-label={`${spec.components.title} board, ${rows} rows by ${cols} columns`}
        className="board__grid"
      >
        {renderedRows}
      </div>

      {columnControls ? (
        <div className="board__columns">
          {Array.from({ length: cols }, (_, column) => (
            <div
              key={column}
              className="board__columnSlot"
              style={{ gridColumn: column + 1 }}
              onMouseEnter={() => setHoveredColumn(column)}
            >
              <Column
                index={column}
                cols={cols}
                rows={rows}
                playable={interactive && legalColumns.includes(column)}
                full={discsIn(spec, board, column) >= rows}
                focused={focusedColumn === column}
                discsInColumn={discsIn(spec, board, column)}
                onDrop={onDrop}
                onFocusColumn={setFocusedColumn}
                registerRef={registerRef}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
